import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUser } from '../contexts/UserContext';
import { useLanguage, useTranslation } from '../i18n';
import { apiClient } from '../services/api';
import './ProfilePage.css';

interface Referral {
  id: number;
  username: string | null;
  first_name: string;
  last_name: string | null;
  referred_at: string;
  created_at: string;
}

interface ReferralInfo {
  referral_code: string;
  referrals_count: number;
  total_earned: number;
  bonus_per_referral: number;
  referral_link: string;
}

export const ProfilePage: React.FC = () => {
  const { user, isLoading } = useUser();
  const navigate = useNavigate();
  const { language } = useLanguage();
  const t = useTranslation();
  const [stats, setStats] = useState<any>(null);
  const [referralInfo, setReferralInfo] = useState<ReferralInfo | null>(null);
  const [friends, setFriends] = useState<Referral[]>([]);
  const [friendsLoading, setFriendsLoading] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  
  useEffect(() => {
    loadStats();
    loadReferralData();
  }, []);

  const loadStats = async () => {
    try {
      const response = await apiClient.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const loadReferralData = async () => {
    try {
      setFriendsLoading(true);
      
      // Load referral info
      const refInfoResponse = await apiClient.getReferralInfo();
      setReferralInfo(refInfoResponse.referral_info);
      
      // Load friends list
      const friendsResponse = await apiClient.getReferrals(1, 10);
      if (friendsResponse.success) {
        setFriends(friendsResponse.referrals || []);
      }
    } catch (error) {
      console.error('Failed to load referral data:', error);
    } finally {
      setFriendsLoading(false);
    }
  };

  const handleCopyLink = async () => {
    if (referralInfo?.referral_link) {
      try {
        await navigator.clipboard.writeText(referralInfo.referral_link);
        setLinkCopied(true);
        
        // Haptic feedback
        if (window.Telegram?.WebApp?.HapticFeedback) {
          window.Telegram.WebApp.HapticFeedback.notificationOccurred('success');
        }
        
        // Reset copied state after 2 seconds
        setTimeout(() => {
          setLinkCopied(false);
        }, 2000);
      } catch (error) {
        console.error('Failed to copy link:', error);
      }
    }
  };

  const getLocale = () => {
    switch (language) {
      case 'ru': return 'ru-RU';
      case 'es': return 'es-ES';
      default: return 'en-US';
    }
  };

  const formatJoinDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getLocale(), { year: 'numeric', month: 'long', day: 'numeric' });
  };

  const formatFriendDate = (dateString: string): string => {
    const date = new Date(dateString);
    return date.toLocaleDateString(getLocale(), { day: 'numeric', month: 'short', year: 'numeric' });
  };


  if (isLoading || !user) {
    return (
      <div className="page-container profile-page">
        <div className="profile-header">
          <p>{t.common.loading}</p>
        </div>
      </div>
    );
  }

  const userName = user.first_name + (user.last_name ? ` ${user.last_name}` : '');
  const userAvatar = user.avatar_url || '/robot.png';
  const userUsername = user.username ? `@${user.username}` : `ID: ${user.telegram_id}`;
  const memberSince = user.member_since || user.created_at;

  return (
    <div className="page-container profile-page">
      <div className="profile-header">
        <div className="profile-avatar">
          <img src={userAvatar} alt={userName} />
        </div>
        <h1 className="profile-name">{userName}</h1>
        <p className="profile-username">{userUsername}</p>
        <p className="profile-join-date">{t.profile.memberSince} {formatJoinDate(memberSince)}</p>
      </div>

      {/* Stats Overview */}
      <div className="home-stats">
        <div className="home-stat-item">
          <span className="stat-icon">🧘‍♀️</span>
          <div className="stat-details">
            <div className="stat-value">{stats?.streakDays || 0} {t.common.days}</div>
            <div className="stat-label">{t.profile.consecutive}</div>
          </div>
        </div>
        <div className="home-stat-item">
          <span className="stat-icon">💎</span>
          <div className="stat-details">
            <div className="stat-value">{(user as any)?.balance || user?.game_stats?.total_points || 0}</div>
            <div className="stat-label">{t.profile.balance}</div>
          </div>
        </div>
      </div>

      {/* Friends Section */}
      <div className="profile-section friends-section">
        <h2 className="section-title">👥 {t.profile.myFriends}</h2>
        
        {friendsLoading ? (
          <div className="friends-loading">{t.common.loading}</div>
        ) : referralInfo ? (
          <>
            {/* Referral Stats */}
            <div className="referral-stats-cards">
              <div className="referral-stat-card">
                <div className="referral-stat-icon">👥</div>
                <div className="referral-stat-value">{referralInfo.referrals_count}</div>
                <div className="referral-stat-label">{t.profile.friends}</div>
              </div>
              <div className="referral-stat-card">
                <div className="referral-stat-icon">💎</div>
                <div className="referral-stat-value">{referralInfo.total_earned}</div>
                <div className="referral-stat-label">{t.profile.earned}</div>
              </div>
            </div>

            {/* Referral Link Card */}
            <div className="referral-link-card">
              <div className="referral-link-header">
                <span className="referral-link-icon">🎁</span>
                <div className="referral-link-text">
                  <div className="referral-link-title">{t.profile.inviteFriend}</div>
                  <div className="referral-link-subtitle">
                    {t.profile.getCrystalsPerFriend.replace('{amount}', String(referralInfo.bonus_per_referral))}
                  </div>
                </div>
              </div>
              
              {/* Referral Link Display */}
              <div className="referral-link-display">
                <div className="referral-link-url">{referralInfo.referral_link}</div>
                <button 
                  className={`copy-link-button ${linkCopied ? 'copied' : ''}`}
                  onClick={handleCopyLink}
                  title={linkCopied ? t.profile.copied : t.profile.copyLink}
                >
                  {linkCopied ? '✓' : '📋'}
                </button>
              </div>

              <div className="referral-instruction">
                {t.profile.copyAndSend}
              </div>
            </div>

            {/* Friends List */}
            {friends.length > 0 && (
              <div className="friends-list">
                <h3 className="friends-list-title">{t.profile.invitedFriends}</h3>
                {friends.map((friend) => {
                  const friendName = friend.first_name + (friend.last_name ? ` ${friend.last_name}` : '');
                  const friendUsername = friend.username ? `@${friend.username}` : '';
                  const joinedDate = formatFriendDate(friend.referred_at || friend.created_at);
                  
                  return (
                    <div key={friend.id} className="friend-item">
                      <div className="friend-avatar">
                        <div className="friend-avatar-placeholder">
                          {friend.first_name.charAt(0).toUpperCase()}
                        </div>
                      </div>
                      <div className="friend-info">
                        <div className="friend-name">{friendName}</div>
                        {friendUsername && (
                          <div className="friend-username">{friendUsername}</div>
                        )}
                        <div className="friend-date">{t.profile.joined} {joinedDate}</div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}

            {friends.length === 0 && (
              <div className="friends-empty">
                <div className="friends-empty-icon">👋</div>
                <div className="friends-empty-text">
                  {t.profile.noFriendsYet}
                </div>
              </div>
            )}
          </>
        ) : null}
      </div>

      <div className="profile-sections">
        <div className="profile-section">
          <h2 className="section-title">{t.profile.settings}</h2>
          <div className="settings-list">
            <button className="setting-item" onClick={() => navigate('/profile/preferences')}>
              <span className="setting-icon">🧘</span>
              <span className="setting-label">{t.profile.myPreferences}</span>
              <span className="setting-arrow">›</span>
            </button>
            <button className="setting-item" onClick={() => navigate('/profile/notifications')}>
              <span className="setting-icon">🔔</span>
              <span className="setting-label">{t.profile.notifications}</span>
              <span className="setting-arrow">›</span>
            </button>
            <button className="setting-item" onClick={() => navigate('/friends')}>
              <span className="setting-icon">👥</span>
              <span className="setting-label">{t.friendsPage.title}</span>
              <span className="setting-arrow">›</span>
            </button>
            <button className="setting-item" onClick={() => navigate('/profile/language')}>
              <span className="setting-icon">🌐</span>
              <span className="setting-label">{t.profile.language}</span>
              <span className="setting-arrow">›</span>
            </button>
            <button className="setting-item" onClick={() => navigate('/profile/support')}>
              <span className="setting-icon">💬</span>
              <span className="setting-label">{t.profile.support}</span>
              <span className="setting-arrow">›</span>
            </button>
            <button className="setting-item" onClick={() => navigate('/profile/about')}>
              <span className="setting-icon">ℹ️</span>
              <span className="setting-label">{t.profile.aboutApp}</span>
              <span className="setting-arrow">›</span>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
